import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, AlertCircle, Eye, EyeOff } from 'lucide-react';

const C4P_LOGO = 'https://customer-assets.emergentagent.com/job_c4p-quoting-hub/artifacts/dnrg8j6i_C4P%20Transparent.png';
const BG_IMG   = 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=80&w=1200';
const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function LoginPage() {
  const [email, setEmail]             = useState('');
  const [password, setPassword]       = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading]         = useState(false);
  const [ssoLoading, setSsoLoading]   = useState(false);
  const [error, setError]             = useState(null);
  const [oktaEnabled, setOktaEnabled] = useState(false);

  const { login, user, error: authError, setError: setAuthError } = useAuth();
  const navigate    = useNavigate();
  const location    = useLocation();
  const from        = location.state?.from?.pathname || '/dashboard';

  // Redirect if already logged in
  useEffect(() => { if (user) navigate(from, { replace: true }); }, [user, navigate, from]);

  // Show auth context SSO errors (e.g., account deactivated)
  useEffect(() => {
    if (authError) { setError(authError); setAuthError(null); }
  }, [authError, setAuthError]);

  // Check if Okta SSO is enabled
  useEffect(() => {
    fetch(`${API_BASE}/api/auth/okta/config`)
      .then(r => r.json())
      .then(d => setOktaEnabled(d.enabled))
      .catch(() => setOktaEnabled(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { forceReset } = await login(email, password);
      if (forceReset) navigate('/auth/reset-password', { replace: true });
      else            navigate(from, { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOktaLogin = () => {
    setSsoLoading(true);
    // Use window.location.origin so the browser stays on whatever domain the user
    // is currently on (quote.myc4p.com in production, preview URL in dev).
    // Using API_BASE would navigate to the Emergent host URL and break domain masking.
    window.location.href = `${window.location.origin}/api/auth/okta/login`;
  };

  return (
    <div className="flex h-screen">

      {/* ── Left brand panel ───────────────────────────────────── */}
      <div
        className="hidden lg:flex lg:w-1/2 flex-col justify-between p-10 relative overflow-hidden"
        style={{ background: 'hsl(var(--c4p-navy-900))' }}
      >
        {/* Background photo with strong overlay */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${BG_IMG})`, opacity: 0.12 }}
        />
        {/* Subtle gradient overlay */}
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(135deg, hsl(218 52% 10% / 0.6) 0%, transparent 100%)' }}
        />

        {/* Logo */}
        <div className="relative z-10">
          <img
            src={C4P_LOGO}
            alt="Cloud4Partners"
            className="h-14 w-auto object-contain"
          />
        </div>

        {/* Tagline */}
        <div className="relative z-10">
          <h1
            className="text-3xl font-bold leading-snug mb-4"
            style={{ fontFamily: 'var(--font-display)', color: 'rgba(255,255,255,0.92)' }}
          >
            Quote smarter,<br />close faster.
          </h1>
          <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.5)' }}>
            Build accurate, professional quotes for SAP Partner customers
            with live pricing previews and AI assistance.
          </p>
        </div>

        {/* Feature pills */}
        <div className="relative z-10 flex gap-3 flex-wrap">
          {[
            ['Multi-currency', 'GBP · EUR · USD · CAD · AUD'],
            ['Live pricing',   'Real-time quote totals'],
            ['AI Assist',      'Natural language quotes'],
          ].map(([title, desc]) => (
            <div key={title} className="rounded-xl px-3 py-2" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <div className="text-xs font-bold" style={{ color: 'hsl(var(--c4p-brand-blue))' }}>{title}</div>
              <div className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.45)' }}>{desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Right login panel ───────────────────────────────────── */}
      <div className="flex flex-col justify-center w-full lg:w-1/2 px-6 sm:px-12 lg:px-16 bg-white">

        {/* Mobile logo */}
        <div className="flex justify-center mb-10 lg:hidden">
          <img src={C4P_LOGO} alt="Cloud4Partners" className="h-12 w-auto object-contain" />
        </div>

        <div className="max-w-sm w-full mx-auto">
          <div className="mb-8">
            <h2
              className="text-2xl font-bold"
              style={{ fontFamily: 'var(--font-display)', color: 'hsl(var(--c4p-navy-900))' }}
            >
              Welcome back
            </h2>
            <p className="text-sm mt-1.5" style={{ color: 'hsl(var(--muted-fg))' }}>
              Sign in to access the quoting portal
            </p>
          </div>

          {error && (
            <Alert variant="destructive" className="mb-5">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription data-testid="login-error-message">{error}</AlertDescription>
            </Alert>
          )}

          {/* Okta SSO button - shown when Okta is configured */}
          {oktaEnabled && (
            <div className="mb-6">
              <Button
                type="button"
                className="w-full font-semibold gap-2"
                onClick={handleOktaLogin}
                disabled={ssoLoading}
                style={{ background: 'hsl(var(--c4p-brand-blue))', color: 'white', height: '44px', fontSize: '14px' }}
                data-testid="login-okta-button"
              >
                {ssoLoading ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Redirecting to Okta...</>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="16" cy="16" r="16" fill="white" fillOpacity="0.2"/>
                      <circle cx="16" cy="16" r="7" fill="white"/>
                    </svg>
                    Sign in with Okta
                  </>
                )}
              </Button>
              <div className="flex items-center gap-3 mt-5 mb-5">
                <div className="flex-1 h-px" style={{ background: 'hsl(var(--border))' }} />
                <span className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>or sign in with email</span>
                <div className="flex-1 h-px" style={{ background: 'hsl(var(--border))' }} />
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-sm font-semibold">Email address</Label>
              <Input
                id="email" type="email" placeholder="you@myc4p.com"
                value={email} onChange={e => setEmail(e.target.value)}
                required autoFocus disabled={loading}
                data-testid="login-email-input"
                style={{ height: '44px', fontSize: '14px' }}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-sm font-semibold">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password} onChange={e => setPassword(e.target.value)}
                  required disabled={loading}
                  className="pr-10"
                  style={{ height: '44px', fontSize: '14px' }}
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{ color: 'hsl(var(--muted-fg))' }}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full font-semibold"
              disabled={loading || !email || !password}
              style={{ background: 'hsl(var(--c4p-navy-900))', color: 'white', height: '44px', fontSize: '14px' }}
              data-testid="login-submit-button"
            >
              {loading ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Signing in...</>
              ) : (
                'Sign in'
              )}
            </Button>
          </form>

          <p className="text-xs text-center mt-8" style={{ color: 'hsl(var(--muted-fg))' }}>
            Internal use only &middot; Cloud4Partners &trade; {new Date().getFullYear()}
          </p>
        </div>
      </div>
    </div>
  );
}
